import React from 'react';
import { Chart } from 'react-chartjs-2';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';

import { getXAxisConfig, ColorsList, percentageFormatter } from '../Chart';
import statusCodesDictionary from './statusCodesDictionary';
import { useIsPrinting } from '~library/print';

export const MAIN_CHART_ID = 'status-codes-main-chart';

type StatusCodesMainChartProps = {
  intervals: string[];
  granularity: AggregatedMetrics.IntervalGranularity;
  statusCodesData: [string, number[]][];
  totalResponsesPerPeriod: number[];
};

function StatusCodesMainChart({
  intervals,
  granularity,
  totalResponsesPerPeriod,
  statusCodesData,
}: StatusCodesMainChartProps) {
  const { isPrinting } = useIsPrinting();
  const statusCodesDatasets = React.useMemo(
    () =>
      statusCodesData.map(([statusCode, data], i) => ({
        label: statusCode,
        data: data.map((value, index) => +(value / totalResponsesPerPeriod[index]) * 100),
        backgroundColor: ColorsList[i],
        borderColor: ColorsList[i],
      })),
    [statusCodesData, totalResponsesPerPeriod]
  );

  return (
    <Chart
      id={MAIN_CHART_ID}
      type="bar"
      data={{
        labels: intervals,
        datasets: statusCodesDatasets,
      }}
      options={{
        animation: isPrinting ? false : undefined,
        plugins: {
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.parsed.y ?? 0;
                if (!context.dataset.label || !statusCodesDictionary[context.dataset.label])
                  return `Other: ${percentageFormatter(value)}`;

                return `${statusCodesDictionary[context.dataset.label]} (${
                  context.dataset.label
                }): ${percentageFormatter(value)}`;
              },
            },
          },
        },
        scales: {
          x: { ...getXAxisConfig(granularity), stacked: true },
          y: {
            stacked: true,
            type: 'linear',
            min: 0,
            max: 100,
            ticks: {
              stepSize: 25,
              callback: (value) => value + '%',
            },
            grid: {
              drawOnChartArea: false, // avoids double gridlines
            },
          },
        },
      }}
    />
  );
}

export default StatusCodesMainChart;
